/* istanbul ignore file */
import React from 'react';
import cn from 'classnames';

import Element from '../Element/Element';
import { supportedBreakpoints as breakpointNames } from '../../shared/breakpoints';

import { columnDefinitionAtBreakpoints } from './GridBagHelpers';
import { CreateProps } from '../../types/utils/CreateProps';

import styles from './GridBag.module.scss';

export interface BreakpointDefinitionsShape {
  xxxs: number | undefined;
  xxs: number | undefined;
  xs: number | undefined;
  sm: number | undefined;
  md: number | undefined;
  lg: number | undefined;
  xlg: number | undefined;
  xxlg: number | undefined;
}

// Adds column information to the element for different breakpoints
const breakpointColumnDataAttribute = (
  columnsAtBreakpoints: BreakpointDefinitionsShape
) => {
  const entries = columnDefinitionAtBreakpoints(columnsAtBreakpoints).map(
    (columns, index) => {
      return `${breakpointNames[index]}-${columns}`;
    }
  );
  return { 'data-col': entries.join(' ') };
};

// Adds last row data
const breakPointLastRowDataAttribute = (lastRowAtBreakpoints?: boolean[]) => {
  const entries = breakpointNames.reduce(
    (dataAttribute: string[], breakpoint: string, index: number) =>
      lastRowAtBreakpoints && lastRowAtBreakpoints[index]
        ? [...dataAttribute, breakpoint]
        : dataAttribute,
    []
  );

  return entries.length > 0 ? { 'data-last-row': entries.join(' ') } : {};
};

// Adds last column data
const breakPointLastColDataAttribute = (
  lastColumnAtBreakpoints?: boolean[]
) => {
  const entries = breakpointNames.reduce(
    (dataAttribute: string[], breakpoint: string, index: number) =>
      lastColumnAtBreakpoints && lastColumnAtBreakpoints[index]
        ? [...dataAttribute, breakpoint]
        : dataAttribute,
    []
  );

  return entries.length > 0 ? { 'data-last-col': entries.join(' ') } : {};
};

export type GridBagItemProps = CreateProps<
  {
    xxxs?: number;
    xxs?: number;
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xlg?: number;
    xxlg?: number;
    /** Remove space around the children */
    noGutter?: boolean;
    /** Remove horizontal space around the children */
    noHorizontalGutter?: boolean;
    /** Remove vertical space around the children */
    noVerticalGutter?: boolean;
    onLastColumn?: boolean[];
    onLastRow?: boolean[];
    /** HTML tag name: div, span, h1 etc */
    tagName?: keyof JSX.IntrinsicElements;
  },
  typeof Element
>;

const GridBagItem: React.FunctionComponent<GridBagItemProps> = ({
  xxxs,
  xxs,
  xs,
  sm,
  md,
  lg,
  xlg,
  xxlg,
  children,
  noHorizontalGutter,
  noVerticalGutter,
  onLastColumn,
  onLastRow,
  className,
  tagName = 'div',
  ...restProps
}) => {
  const breakpointDefinitions = { xxxs, xxs, xs, sm, md, lg, xlg, xxlg };

  const itemClasses = cn(
    styles.gridBag_item,
    ...(noHorizontalGutter ? [styles.noHorizontalGutter] : []),
    ...(noVerticalGutter ? [styles.noVerticalGutter] : []),
    className
  );

  const columnDataAttribute = breakpointColumnDataAttribute(
    breakpointDefinitions
  );
  const lastRowDataAttribute = breakPointLastRowDataAttribute(onLastRow);
  const lastColDataAttribute = breakPointLastColDataAttribute(onLastColumn);

  return (
    <Element
      className={itemClasses}
      tagName={tagName}
      {...columnDataAttribute}
      {...lastRowDataAttribute}
      {...lastColDataAttribute}
      {...restProps}
    >
      {children}
    </Element>
  );
};

export default GridBagItem;
